import type { CogniteClient, NodeOrEdge, ViewReference } from '@cognite/sdk';

import type { DMSNodeResponse } from '../../types/dms-types';

import type { InFieldCDMLocationConfig } from './cdm-types';

const hasDMSResponseProperties = (
  space: string,
  viewIdentifier: string,
  config?: NodeOrEdge
): config is InFieldLocationConfigDMS => {
  if (!config) return false;
  return (
    'properties' in config &&
    config.properties?.[space]?.[viewIdentifier] !== undefined
  );
};

export type InFieldLocationConfigDMS =
  DMSNodeResponse<InFieldCDMLocationConfig>;

export type CogniteClientDependencies = {
  instances: Pick<CogniteClient['instances'], 'list'>;
};

export interface IInfieldAppConfigService {
  listAll(): Promise<InFieldCDMLocationConfig[]>;
}

/**
 * Service for managing InField location configuration data.
 * Provides methods to retrieve location configurations from the CDF view.
 *
 * @example
 * ```typescript
 * const config = new InfieldAppConfigService(cogniteClient, cdfView);
 * const locationConfig = await config.retrieveByLocationExternalId('location-123');
 * ```
 */
export class InfieldAppConfigService implements IInfieldAppConfigService {
  cogniteClient: CogniteClientDependencies;
  viewReference: ViewReference;

  viewIdentifier: string;

  /**
   * Creates a new instance of InfieldAppConfigService.
   *
   * @param client - The Cognite client instance for making API calls
   * @param viewReference - The CDF view reference for the InField location config
   */
  constructor(
    client: {
      instances: Pick<CogniteClient['instances'], 'list'>;
    },
    viewReference: ViewReference
  ) {
    this.cogniteClient = client;
    this.viewReference = viewReference;
    this.viewIdentifier = `${this.viewReference.externalId}/${this.viewReference.version}`;
  }

  /**
   * Retrieves all InField location configurations from the DMS view.
   *
   * @returns An array of all location configurations, or `undefined` if the response
   *   doesn't match the expected InField config structure.
   */
  async listAll(): Promise<InFieldCDMLocationConfig[]> {
    const infieldConfigs = await this.cogniteClient.instances
      .list({
        instanceType: 'node',
        sources: [
          {
            source: this.viewReference,
          },
        ],
      })
      .then((res) => res.items);

    const space = this.viewReference.space;
    const validInfieldConfigs = infieldConfigs.filter((config) =>
      hasDMSResponseProperties(space, this.viewIdentifier, config)
    );

    return validInfieldConfigs.map((config) => {
      return {
        ...config.properties?.[space][this.viewIdentifier],
        space: config.space,
        externalId: config.externalId,
      } satisfies InFieldCDMLocationConfig;
    });
  }
}

/**
 * Factory function for creating InfieldAppConfigService instances.
 * This allows dependency injection for testing purposes.
 */
export const createInfieldAppConfigService = (
  ...args: ConstructorParameters<typeof InfieldAppConfigService>
): IInfieldAppConfigService => new InfieldAppConfigService(...args);
